import { useState, useRef, useEffect } from 'react';
import GlowButton from '@/components/base/GlowButton';
import ModalOverlay from '@/components/base/ModalOverlay';

interface ReportModalProps {
  open: boolean;
  communityName: string;
  onClose: () => void;
}

const reportReasons = [
  { id: 'spam', label: 'Spam or Scam', icon: 'ri-spam-2-line' },
  { id: 'inappropriate', label: 'Inappropriate Content', icon: 'ri-error-warning-line' },
  { id: 'fake', label: 'Fake or Misleading Info', icon: 'ri-file-damage-line' },
  { id: 'inactive', label: 'Community is Inactive', icon: 'ri-time-line' },
  { id: 'hate', label: 'Hate Speech or Harassment', icon: 'ri-shield-cross-line' },
  { id: 'other', label: 'Other', icon: 'ri-more-line' },
];

type Step = 'reason' | 'detail' | 'done';

const STEP_ORDER: Step[] = ['reason', 'detail', 'done'];

export default function ReportModal({ open, communityName, onClose }: ReportModalProps) {
  const [step, setStep] = useState<Step>('reason');
  const [prevStep, setPrevStep] = useState<Step>('reason');
  const [animating, setAnimating] = useState(false);
  const [slideDir, setSlideDir] = useState<'left' | 'right'>('left');
  const [selectedReason, setSelectedReason] = useState('');
  const [detail, setDetail] = useState('');
  const [charCount, setCharCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep('reason');
        setPrevStep('reason');
        setSelectedReason('');
        setDetail('');
        setCharCount(0);
        setAnimating(false);
      }, 300);
    }
  }, [open]);

  const goTo = (next: Step) => {
    if (animating) return;
    const curIdx = STEP_ORDER.indexOf(step);
    const nextIdx = STEP_ORDER.indexOf(next);
    setSlideDir(nextIdx > curIdx ? 'left' : 'right');
    setPrevStep(step);
    setAnimating(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setStep(next);
      setAnimating(false);
    }, 260);
  };

  const handleDetailChange = (v: string) => {
    if (v.length <= 500) {
      setDetail(v);
      setCharCount(v.length);
    }
  };

  const handleSubmit = () => goTo('done');

  return (
    <ModalOverlay open={open} onClose={onClose} zIndex={100}>
      <div className="w-full max-w-md bg-white/75 backdrop-blur-2xl border border-white/90 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/60">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50/80 backdrop-blur-sm border border-red-100/80">
              <i className="ri-flag-2-line text-red-500 text-sm"></i>
            </span>
            <div>
              <h3 className="text-sm font-bold text-slate-800">Report Community</h3>
              <p className="text-[11px] text-slate-400 truncate max-w-[220px]">{communityName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-white/60 backdrop-blur-sm rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Step slide wrapper */}
        <div className="p-5 overflow-hidden">
          <div
            style={{
              transition: animating ? 'opacity 0.26s ease, transform 0.26s cubic-bezier(0.22,1,0.36,1)' : 'none',
              opacity: animating ? 0 : 1,
              transform: animating
                ? `translateX(${slideDir === 'left' ? '-28px' : '28px'})`
                : 'translateX(0)',
            }}
          >
          {step === 'reason' && (
            <>
              <p className="text-sm text-slate-500 mb-4">What&apos;s the issue with this community?</p>
              <div className="flex flex-col gap-2">
                {reportReasons.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setSelectedReason(r.id)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all cursor-pointer ${
                      selectedReason === r.id
                        ? 'bg-red-50/70 backdrop-blur-sm border-red-200/70 text-red-700'
                        : 'bg-white/50 backdrop-blur-sm border-white/70 text-slate-600 hover:border-[#b8d4ea]/60 hover:bg-white/70'
                    }`}
                  >
                    <span className={`w-5 h-5 flex items-center justify-center shrink-0 ${selectedReason === r.id ? 'text-red-500' : 'text-slate-400'}`}>
                      <i className={`${r.icon} text-sm`}></i>
                    </span>
                    <span className="text-sm font-medium">{r.label}</span>
                    {selectedReason === r.id && (
                      <span className="ml-auto w-4 h-4 flex items-center justify-center shrink-0">
                        <i className="ri-check-line text-red-500 text-sm"></i>
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <GlowButton
                  onClick={() => selectedReason && goTo('detail')}
                  disabled={!selectedReason}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold"
                >
                  Continue
                </GlowButton>
              </div>
            </>
          )}

          {step === 'detail' && (
            <>
              <button
                onClick={() => goTo('reason')}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 mb-4 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                Back
              </button>
              <p className="text-sm font-semibold text-slate-700 mb-1">
                {reportReasons.find((r) => r.id === selectedReason)?.label}
              </p>
              <p className="text-xs text-slate-400 mb-3">Please provide additional details to help us review this report.</p>
              <textarea
                value={detail}
                onChange={(e) => handleDetailChange(e.target.value)}
                placeholder="Describe the issue in detail..."
                rows={4}
                className="w-full bg-white/50 backdrop-blur-sm border border-white/70 rounded-xl px-4 py-3 text-sm text-slate-700 placeholder-slate-400 outline-none focus:border-[#0088CC]/40 focus:bg-white/70 resize-none transition-colors"
              />
              <div className="flex justify-between items-center mt-1 mb-4">
                <span className="text-[11px] text-slate-400">Optional but helpful</span>
                <span className={`text-[11px] ${charCount >= 450 ? 'text-red-400' : 'text-slate-400'}`}>{charCount}/500</span>
              </div>
              <GlowButton onClick={handleSubmit} className="w-full py-2.5 rounded-xl text-sm font-semibold">
                Submit Report
              </GlowButton>
            </>
          )}

          {step === 'done' && (
            <div className="flex flex-col items-center text-center py-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-50/80 backdrop-blur-sm border border-emerald-100/80 mb-4">
                <i className="ri-check-double-line text-emerald-500 text-2xl"></i>
              </div>
              <h4 className="text-base font-bold text-slate-800 mb-2">Report Submitted</h4>
              <p className="text-sm text-slate-400 leading-relaxed mb-5">
                Thank you for helping keep TGDirectory safe. Our team will review this report within 24–48 hours.
              </p>
              <GlowButton onClick={onClose} className="px-6 py-2.5 text-sm font-semibold rounded-xl">
                Done
              </GlowButton>
            </div>
          )}
          </div>{/* end slide wrapper */}
        </div>
      </div>
    </ModalOverlay>
  );
}
